/**
 * Advanced Media Relevance Engine
 * Implements weighted scoring with location hierarchy and fuzzy matching
 */

import Fuse from 'fuse.js';
import geoHierarchy from '../config/geo_hierarchy.json';
import synonyms from '../config/synonyms.json';

class RelevanceEngine {
  constructor() {
    this.geoHierarchy = geoHierarchy;
    this.synonyms = synonyms;
    this.minRelevanceScore = 5; // Minimum score threshold to consider article relevant
    
    // Scoring weights
    this.weights = {
      keywordInTitle: 3,
      keywordInBody: 2,
      synonymInTitle: 2,
      synonymInBody: 1,
      locationInTitle: 2,
      locationInBody: 1,
      locationHierarchyMatch: 1,
      recentArticle6h: 2,
      recentArticle24h: 1,
      fuzzyMatchFallback: 1
    };
    
    console.log('🧠 Relevance Engine initialized with geo hierarchy and synonyms');
  }

  /**
   * Score an article's relevance to an incident
   * @param {Object} article - Article object with title, description, link, pubDate
   * @param {Object} incident - Incident object with keywords, locations, timestamp
   * @returns {Object} - { score, explanation, isRelevant }
   */
  scoreArticle(article, incident) {
    try {
      let score = 0;
      const explanation = [];
      
      const articleText = this.getArticleText(article);
      const articleTitle = (article.title || '').toLowerCase();
      const articleBody = articleText.toLowerCase();
      
      // Expand incident data
      const expandedKeywords = this.expandKeywords(incident.keywords || []);
      const expandedLocations = this.expandLocations(incident.locations || []);
      
      // 1. Keyword matching in title
      const titleKeywordMatches = this.findMatches(expandedKeywords.exact, articleTitle);
      score += titleKeywordMatches.length * this.weights.keywordInTitle;
      if (titleKeywordMatches.length > 0) {
        explanation.push(`Keywords in title: ${titleKeywordMatches.join(', ')} (+${titleKeywordMatches.length * this.weights.keywordInTitle})`);
      }
      
      // 2. Keyword matching in body
      const bodyKeywordMatches = this.findMatches(expandedKeywords.exact, articleBody);
      score += bodyKeywordMatches.length * this.weights.keywordInBody;
      if (bodyKeywordMatches.length > 0) {
        explanation.push(`Keywords in body: ${bodyKeywordMatches.join(', ')} (+${bodyKeywordMatches.length * this.weights.keywordInBody})`);
      }
      
      // 3. Synonym matching in title
      const titleSynonymMatches = this.findMatches(expandedKeywords.synonyms, articleTitle);
      score += titleSynonymMatches.length * this.weights.synonymInTitle;
      if (titleSynonymMatches.length > 0) {
        explanation.push(`Synonyms in title: ${titleSynonymMatches.join(', ')} (+${titleSynonymMatches.length * this.weights.synonymInTitle})`);
      }
      
      // 4. Synonym matching in body
      const bodySynonymMatches = this.findMatches(expandedKeywords.synonyms, articleBody);
      score += bodySynonymMatches.length * this.weights.synonymInBody;
      if (bodySynonymMatches.length > 0) {
        explanation.push(`Synonyms in body: ${bodySynonymMatches.join(', ')} (+${bodySynonymMatches.length * this.weights.synonymInBody})`);
      }
      
      // 5. Location matching in title
      const titleLocationMatches = this.findMatches(expandedLocations.all, articleTitle);
      score += titleLocationMatches.length * this.weights.locationInTitle;
      if (titleLocationMatches.length > 0) {
        explanation.push(`Locations in title: ${titleLocationMatches.join(', ')} (+${titleLocationMatches.length * this.weights.locationInTitle})`);
      }
      
      // 6. Location matching in body
      const bodyLocationMatches = this.findMatches(expandedLocations.all, articleBody);
      score += bodyLocationMatches.length * this.weights.locationInBody;
      if (bodyLocationMatches.length > 0) {
        explanation.push(`Locations in body: ${bodyLocationMatches.join(', ')} (+${bodyLocationMatches.length * this.weights.locationInBody})`);
      }
      
      // 7. Time-based scoring
      const timeScore = this.calculateTimeScore(article.pubDate, incident.timestamp);
      score += timeScore.score;
      if (timeScore.score > 0) {
        explanation.push(`${timeScore.explanation} (+${timeScore.score})`);
      }
      
      // 8. Fuzzy matching fallback (only if current score is low)
      if (score < this.minRelevanceScore) {
        const fuzzyScore = this.calculateFuzzyScore(articleText, expandedKeywords, expandedLocations);
        score += fuzzyScore.score;
        if (fuzzyScore.score > 0) {
          explanation.push(`${fuzzyScore.explanation} (+${fuzzyScore.score})`);
        }
      }
      
      const isRelevant = score >= this.minRelevanceScore;
      
      return {
        score: Math.round(score * 10) / 10, // Round to 1 decimal place
        explanation: explanation.join('; '),
        isRelevant,
        details: {
          titleKeywords: titleKeywordMatches,
          bodyKeywords: bodyKeywordMatches,
          titleSynonyms: titleSynonymMatches,
          bodySynonyms: bodySynonymMatches,
          titleLocations: titleLocationMatches,
          bodyLocations: bodyLocationMatches
        }
      };
      
    } catch (error) {
      console.error('❌ Error scoring article:', error);
      return {
        score: 0,
        explanation: `Error scoring article: ${error.message}`,
        isRelevant: false,
        details: {}
      };
    }
  }

  /**
   * Extract full text from article object
   */
  getArticleText(article) {
    const title = article.title || '';
    const description = article.description || article.summary || '';
    const content = article.content || article.contentSnippet || '';
    
    return `${title} ${description} ${content}`.trim();
  }

  /**
   * Expand keywords using synonyms
   */
  expandKeywords(keywords) {
    const exact = keywords.map(k => k.toLowerCase());
    const synonyms = [];
    
    for (const keyword of exact) {
      // Check if keyword has synonyms
      if (this.synonyms[keyword]) {
        synonyms.push(...this.synonyms[keyword].map(s => s.toLowerCase()));
      }
      
      // Check if keyword is a synonym of something else
      for (const [mainTerm, syns] of Object.entries(this.synonyms)) {
        if (syns.map(s => s.toLowerCase()).includes(keyword)) {
          synonyms.push(mainTerm.toLowerCase());
          synonyms.push(...syns.map(s => s.toLowerCase()));
        }
      }
    }
    
    return {
      exact: [...new Set(exact)],
      synonyms: [...new Set(synonyms)],
      all: [...new Set([...exact, ...synonyms])]
    };
  }

  /**
   * Expand locations using geographic hierarchy
   */
  expandLocations(locations) {
    const exact = locations.map(l => l.toLowerCase());
    const hierarchical = [];
    
    for (const location of exact) {
      // Find exact matches in hierarchy
      const hierarchy = this.geoHierarchy[this.capitalizeLocation(location)];
      if (hierarchy) {
        hierarchical.push(...hierarchy.map(h => h.toLowerCase()));
      }
      
      // Find if location appears in any hierarchy (reverse lookup)
      for (const [place, hierarchy] of Object.entries(this.geoHierarchy)) {
        if (hierarchy.map(h => h.toLowerCase()).includes(location)) {
          hierarchical.push(place.toLowerCase());
          hierarchical.push(...hierarchy.map(h => h.toLowerCase()));
        }
      }
    }
    
    return {
      exact: [...new Set(exact)],
      hierarchical: [...new Set(hierarchical)],
      all: [...new Set([...exact, ...hierarchical])]
    };
  }

  /**
   * Capitalize location for hierarchy lookup
   */
  capitalizeLocation(location) {
    return location.split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Find matches of terms in text
   */
  findMatches(terms, text) {
    const matches = [];
    for (const term of terms) {
      if (text.includes(term.toLowerCase())) {
        matches.push(term);
      }
    }
    return [...new Set(matches)];
  }

  /**
   * Calculate time-based scoring
   */
  calculateTimeScore(articleDate, incidentDate) {
    try {
      const articleTime = new Date(articleDate);
      const incidentTime = new Date(incidentDate);
      const timeDiff = Math.abs(articleTime - incidentTime) / (1000 * 60 * 60); // Hours
      
      if (timeDiff <= 6) {
        return { score: this.weights.recentArticle6h, explanation: 'Recent article (< 6h)' };
      } else if (timeDiff <= 24) {
        return { score: this.weights.recentArticle24h, explanation: 'Recent article (< 24h)' };
      }
      
      return { score: 0, explanation: 'Article not recent' };
    } catch (error) {
      return { score: 0, explanation: 'Could not parse article date' };
    }
  }

  /**
   * Calculate fuzzy matching score as fallback
   */
  calculateFuzzyScore(articleText, expandedKeywords, expandedLocations) {
    try {
      const allTerms = [...expandedKeywords.all, ...expandedLocations.all];
      
      if (allTerms.length === 0) {
        return { score: 0, explanation: 'No terms for fuzzy matching' };
      }
      
      const fuse = new Fuse(allTerms, {
        includeScore: true,
        threshold: 0.3, // 0 = exact match, 1 = match anything
        keys: ['term']
      });
      
      const words = articleText.toLowerCase().split(/\s+/);
      let fuzzyMatches = 0;
      const matchedTerms = [];
      
      for (const word of words) {
        const results = fuse.search(word);
        if (results.length > 0 && results[0].score < 0.3) {
          fuzzyMatches++;
          matchedTerms.push(`${word}~${results[0].item}`);
        }
      }
      
      const score = Math.min(fuzzyMatches * this.weights.fuzzyMatchFallback, 3); // Cap at 3 points
      
      if (score > 0) {
        return { 
          score, 
          explanation: `Fuzzy matches: ${matchedTerms.slice(0, 3).join(', ')}${matchedTerms.length > 3 ? '...' : ''}` 
        };
      }
      
      return { score: 0, explanation: 'No fuzzy matches found' };
    } catch (error) {
      console.warn('⚠️ Fuzzy matching error:', error);
      return { score: 0, explanation: 'Fuzzy matching failed' };
    }
  }

  /**
   * Filter articles by relevance score
   * @param {Array} articles - Array of articles
   * @param {Object} incident - Incident object
   * @returns {Array} - Filtered and scored articles
   */
  filterRelevantArticles(articles, incident) {
    if (!articles || articles.length === 0) {
      return [];
    }
    
    console.log(`🔍 Scoring ${articles.length} articles for relevance...`);
    
    const scoredArticles = articles.map(article => {
      const relevanceResult = this.scoreArticle(article, incident);
      return {
        ...article,
        relevanceScore: relevanceResult.score,
        relevanceExplanation: relevanceResult.explanation,
        isRelevant: relevanceResult.isRelevant,
        relevanceDetails: relevanceResult.details
      };
    });
    
    // Filter relevant articles and sort by score
    const relevantArticles = scoredArticles
      .filter(article => article.isRelevant)
      .sort((a, b) => b.relevanceScore - a.relevanceScore);
    
    console.log(`✅ Found ${relevantArticles.length} relevant articles out of ${articles.length} total`);
    
    // Log debug info for rejected articles (limit to first 5)
    const rejectedArticles = scoredArticles.filter(article => !article.isRelevant);
    if (rejectedArticles.length > 0) {
      console.log(`ℹ️ Top rejected articles:`);
      rejectedArticles.slice(0, 5).forEach(article => {
        console.log(`  - "${article.title}" (Score: ${article.relevanceScore}) - ${article.relevanceExplanation}`);
      });
    }
    
    return relevantArticles;
  }

  /**
   * Update minimum relevance score threshold
   */
  setMinRelevanceScore(score) {
    this.minRelevanceScore = score;
    console.log(`🎯 Updated minimum relevance score to: ${score}`);
  }

  /**
   * Get current configuration
   */
  getConfig() {
    return {
      minRelevanceScore: this.minRelevanceScore,
      weights: this.weights,
      synonymsCount: Object.keys(this.synonyms).length,
      geoHierarchyCount: Object.keys(this.geoHierarchy).length
    };
  }
}

// Export singleton instance
export default new RelevanceEngine();